/** @jsxImportSource @emotion/react */
import { toast } from '@app/components/ui/Toast/toast'
import { LayerMetadata } from '@app/types/layerTypes'
import { zodResolver } from '@hookform/resolvers/zod'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useUpsertLayerMetadata } from '../hooks/useUpsertLayerMetadata'
import { LayerMetadataFormValues, layerMetadataSchema } from '../types/layers.types'
import * as styles from './LayerMetadataForm.styles'

type Props = {
  layerId: string
  defaultValues?: Partial<LayerMetadata> | null
}

const CONFIDENCE_OPTIONS = ['A', 'B', 'C', 'D'] as const

export const LayerMetadataForm = ({ layerId, defaultValues }: Props) => {
  const { mutate, isPending } = useUpsertLayerMetadata()
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LayerMetadataFormValues>({
    resolver: zodResolver(layerMetadataSchema),
    mode: 'onBlur',
    defaultValues: {
      title: defaultValues?.title ?? '',
      description_md: defaultValues?.description_md ?? '',
      provider: defaultValues?.provider ?? '',
      spatial_extent: defaultValues?.spatial_extent ?? '',
      created_at: defaultValues?.created_at ?? '',
      owner_name: defaultValues?.owner_name ?? '',
      owner_organization: defaultValues?.owner_organization ?? '',
      owner_email: defaultValues?.owner_email ?? '',
      confidence_rating: CONFIDENCE_OPTIONS.includes(
        defaultValues?.confidence_rating as 'A' | 'B' | 'C' | 'D',
      )
        ? (defaultValues?.confidence_rating as 'A' | 'B' | 'C' | 'D')
        : '',
      limitations_caveats_md: defaultValues?.limitations_caveats_md ?? '',
      source_links: defaultValues?.source_links ?? [],
      license: defaultValues?.license ?? { title: '', url: '' },
      additional_notes_md: defaultValues?.additional_notes_md ?? '',
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'source_links',
  })

  const onSubmit = (body: LayerMetadataFormValues) => {
    const sanitised = JSON.parse(JSON.stringify(body))

    mutate(
      { layerId, body: sanitised },
      {
        onSuccess: () => {
          navigate(-1)
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to save metadata',
            position: 'top-center',
            type: 'error',
          })
        },
      },
    )
  }

  const onCancel = () => {
    navigate(-1)
  }
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} css={styles.formRoot}>
      <Stack spacing={2} css={styles.formStack}>
        <Box css={styles.fieldsSection}>
          <Box css={styles.section}>
            <Typography css={styles.sectionLabel}>Basic</Typography>
            <Box css={styles.fieldsGrid}>
              <TextField
                {...register('title')}
                label="Title"
                fullWidth
                size="small"
                css={[styles.fieldControl, styles.fullWidthField]}
              />
              <TextField
                {...register('description_md')}
                label="Description (Markdown)"
                fullWidth
                size="small"
                multiline
                minRows={4}
                css={[styles.multilineField, styles.fullWidthField]}
              />
              <TextField
                {...register('spatial_extent')}
                label="Spatial extent"
                fullWidth
                size="small"
                css={styles.fieldControl}
              />
              <TextField
                {...register('provider')}
                label="Provider"
                fullWidth
                size="small"
                css={styles.fieldControl}
              />
              <TextField
                {...register('created_at')}
                label="Model date"
                fullWidth
                size="small"
                css={styles.fieldControl}
              />
            </Box>
          </Box>

          <Divider css={styles.divider} />

          <Box css={styles.section}>
            <Typography css={styles.sectionLabel}>Ownership</Typography>
            <Box css={styles.fieldsGrid}>
              <TextField
                {...register('owner_name')}
                label="Owner name"
                fullWidth
                size="small"
                css={styles.fieldControl}
              />
              <TextField
                {...register('owner_organization')}
                label="Team / Organization"
                fullWidth
                size="small"
                css={styles.fieldControl}
              />
              <TextField
                {...register('owner_email')}
                label="Email"
                type="email"
                fullWidth
                size="small"
                css={styles.fieldControl}
                error={!!errors.owner_email}
                helperText={errors.owner_email?.message}
              />
            </Box>
          </Box>

          <Divider css={styles.divider} />

          <Box css={styles.section}>
            <Typography css={styles.sectionLabel}>Data quality</Typography>
            <Box css={styles.fieldsGrid}>
              <Controller
                name="confidence_rating"
                control={control}
                render={({ field }) => (
                  <Select {...field} size="small" displayEmpty fullWidth css={styles.fieldControl}>
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {CONFIDENCE_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <TextField
                {...register('limitations_caveats_md')}
                label="Limitations / caveats (Markdown)"
                fullWidth
                size="small"
                multiline
                minRows={3}
                css={[styles.multilineField, styles.fullWidthField]}
              />
            </Box>
          </Box>

          <Divider css={styles.divider} />

          <Box css={styles.section}>
            <Typography css={styles.sectionLabel}>Sources</Typography>
            <Stack css={styles.sourceLinksStack}>
              {fields.map((field, index) => (
                <Box key={field.id} css={styles.sourceLinkRow}>
                  <TextField
                    {...register(`source_links.${index}.title`)}
                    label="Title"
                    size="small"
                    css={styles.fieldControl}
                    error={!!errors.source_links?.[index]?.title}
                    helperText={errors.source_links?.[index]?.title?.message}
                  />
                  <TextField
                    {...register(`source_links.${index}.url`)}
                    label="URL"
                    size="small"
                    css={styles.fieldControl}
                    error={!!errors.source_links?.[index]?.url}
                    helperText={errors.source_links?.[index]?.url?.message}
                  />
                  <IconButton size="small" onClick={() => remove(index)} css={styles.deleteButton}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="outlined"
                onClick={() => append({ title: '', url: '' })}
                css={styles.addSourceButton}
              >
                Add source
              </Button>
            </Stack>
          </Box>

          <Divider css={styles.divider} />

          <Divider css={styles.divider} />

          <Box css={styles.section}>
            <Typography css={styles.sectionLabel}>Additional notes</Typography>
            <TextField
              {...register('additional_notes_md')}
              label="Additional notes (Markdown)"
              fullWidth
              size="small"
              multiline
              minRows={3}
              css={styles.multilineField}
            />
          </Box>

          <Box css={styles.actionsRow}>
            <Button
              variant="outlined"
              disabled={isPending}
              css={styles.submitButton}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending}
              css={styles.submitButton}
            >
              {isPending ? 'Saving…' : 'Save metadata'}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}
